/**
 * @fileoverview Vuetify configuration with custom theme
 * @module plugins/vuetify
 *
 * Theme colors are loaded from centralized branding defaults.
 * @see src/config/brandingDefaults.ts for color source of truth
 */
import 'vuetify/styles'
import '@mdi/font/css/materialdesignicons.css'
import { createVuetify, type ThemeDefinition } from 'vuetify'
import { aliases, mdi } from 'vuetify/iconsets/mdi'
import {
  COLOR_DEFAULTS,
  COLOR_DARK_OVERRIDES,
  BREAKPOINT_DEFAULTS,
  COMPONENT_DEFAULTS,
} from '@/config/brandingDefaults'

/**
 * Light theme using centralized branding defaults
 */
const lightTheme: ThemeDefinition = {
  dark: false,
  colors: {
    // Brand Colors (from COLOR_DEFAULTS)
    primary: COLOR_DEFAULTS.primary,
    'primary-darken-1': COLOR_DEFAULTS.primaryDarken1,
    'primary-darken-2': COLOR_DEFAULTS.primaryDarken2,
    'primary-lighten-1': COLOR_DEFAULTS.primaryLighten1,
    secondary: COLOR_DEFAULTS.secondary,
    'secondary-darken-1': COLOR_DEFAULTS.secondaryDarken1,
    'secondary-lighten-1': COLOR_DEFAULTS.secondaryLighten1,
    accent: COLOR_DEFAULTS.accent,
    info: COLOR_DEFAULTS.info,
    error: COLOR_DEFAULTS.error,
    warning: COLOR_DEFAULTS.warning,
    success: COLOR_DEFAULTS.success,
    // Surfaces
    background: COLOR_DEFAULTS.background,
    surface: COLOR_DEFAULTS.surface,
    'surface-bright': COLOR_DEFAULTS.surfaceBright,
    'surface-light': COLOR_DEFAULTS.surfaceLight,
    'surface-variant': COLOR_DEFAULTS.surfaceVariant,
    'on-surface-variant': COLOR_DEFAULTS.onSurfaceVariant,
    // Text on colors
    'on-primary': COLOR_DEFAULTS.onPrimary,
    'on-secondary': COLOR_DEFAULTS.onSecondary,
    'on-background': COLOR_DEFAULTS.onBackground,
    'on-surface': COLOR_DEFAULTS.onSurface,
  },
}

/**
 * Dark theme using centralized branding defaults with dark overrides
 */
const darkTheme: ThemeDefinition = {
  dark: true,
  colors: {
    // Brand Colors (maintained in dark mode)
    primary: COLOR_DEFAULTS.primary,
    'primary-darken-1': COLOR_DEFAULTS.primaryDarken1,
    'primary-darken-2': COLOR_DEFAULTS.primaryDarken2,
    'primary-lighten-1': COLOR_DEFAULTS.primaryLighten1,
    secondary: COLOR_DEFAULTS.secondary,
    'secondary-darken-1': COLOR_DEFAULTS.secondaryDarken1,
    'secondary-lighten-1': COLOR_DEFAULTS.secondaryLighten1,
    accent: COLOR_DEFAULTS.accent,
    info: COLOR_DARK_OVERRIDES.info,
    error: COLOR_DARK_OVERRIDES.error,
    warning: COLOR_DARK_OVERRIDES.warning,
    success: COLOR_DARK_OVERRIDES.success,
    // Surfaces (dark overrides)
    background: COLOR_DARK_OVERRIDES.background,
    surface: COLOR_DARK_OVERRIDES.surface,
    'surface-bright': COLOR_DARK_OVERRIDES.surfaceBright,
    'surface-light': COLOR_DARK_OVERRIDES.surfaceLight,
    'surface-variant': COLOR_DARK_OVERRIDES.surfaceVariant,
    'on-surface-variant': COLOR_DARK_OVERRIDES.onSurfaceVariant,
    // Text on colors (dark overrides - computed for contrast on light brand colors)
    'on-primary': COLOR_DEFAULTS.onBackground,
    'on-secondary': COLOR_DEFAULTS.onBackground,
    'on-background': COLOR_DARK_OVERRIDES.onBackground,
    'on-surface': COLOR_DARK_OVERRIDES.onSurface,
  },
}

export default createVuetify({
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: { mdi },
  },
  theme: {
    defaultTheme: 'light',
    themes: {
      light: lightTheme,
      dark: darkTheme,
    },
  },
  defaults: {
    // Button defaults from COMPONENT_DEFAULTS
    VBtn: {
      variant: 'flat',
      rounded: COMPONENT_DEFAULTS.button.rounded,
    },
    // Card defaults from COMPONENT_DEFAULTS
    VCard: {
      rounded: COMPONENT_DEFAULTS.card.rounded,
      elevation: COMPONENT_DEFAULTS.card.elevation,
    },
    // Input defaults from COMPONENT_DEFAULTS
    VTextField: {
      variant: COMPONENT_DEFAULTS.input.variant,
      density: COMPONENT_DEFAULTS.input.density,
    },
    VSelect: {
      variant: COMPONENT_DEFAULTS.input.variant,
      density: COMPONENT_DEFAULTS.input.density,
    },
    VAutocomplete: {
      variant: COMPONENT_DEFAULTS.input.variant,
      density: COMPONENT_DEFAULTS.input.density,
    },
    VCombobox: {
      variant: COMPONENT_DEFAULTS.input.variant,
      density: COMPONENT_DEFAULTS.input.density,
    },
    VTextarea: {
      variant: COMPONENT_DEFAULTS.input.variant,
      density: COMPONENT_DEFAULTS.input.density,
    },
    // List defaults from COMPONENT_DEFAULTS
    VList: {
      density: COMPONENT_DEFAULTS.list.density,
    },
    // Alert defaults for consistent feedback styling
    VAlert: {
      variant: 'tonal',
      rounded: COMPONENT_DEFAULTS.card.rounded,
    },
    // Chip defaults
    VChip: {
      rounded: 'lg',
    },
    // Dialog defaults
    VDialog: {
      maxWidth: 600,
    },
    // Snackbar defaults
    VSnackbar: {
      timeout: 5000,
    },
  },
  display: {
    mobileBreakpoint: 'sm',
    thresholds: {
      xs: BREAKPOINT_DEFAULTS.xs,
      sm: BREAKPOINT_DEFAULTS.sm,
      md: BREAKPOINT_DEFAULTS.md,
      lg: BREAKPOINT_DEFAULTS.lg,
      xl: BREAKPOINT_DEFAULTS.xl,
      xxl: 2560,
    },
  },
})
